/**
 * @file Filter dialog for the back orders list page.
 */



import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";



// Renders the backorder filter dialog box
/**
 * Renders a dialog for filtering the back orders list by date range and customer.
 *
 * Opens automatically when the component mounts. The start date defaults to two months ago and the end date to today.
 * When the user clicks Apply, the filters are sent to the parent as a query string and the dialog closes.
 *
 * Fields:
 * - Start Date / End Date: The delivery date range (backend: dateStart, dateEnd in BackOrderFilter).
 * - Customer Code: Matches customer codes that start with the entered text (backend: customer).
 * - Item Code: Currently hidden (commented out), but still sent as an empty `item` parameter.
 *
 * Buttons:
 * - Cancel: Closes the dialog without applying changes.
 * - Reset: Clears every field in the dialog. Filters are not applied until the user clicks Apply.
 * - Apply: Sends the filters to the parent and closes the dialog.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {Function} props.setOpen - Sets the dialog's open state. Called with `true` when the component mounts.
 * @param {Function} props.onModalClose - Closes the dialog. Called on Cancel, on Apply, and when the user clicks outside the dialog or presses Escape.
 * @param {Function} props.onChangeFilters - Receives the filter query string, e.g.
 *                                           "&dateStart=2026-09-02&dateEnd=2026-11-02&customer=C100&item=".
 *                                           The parent appends it to the `/backorders/` fetch URL.
 * @returns {JSX.Element} The filter dialog.
 */
function BackOrderDialog({ open, setOpen, onModalClose, onChangeFilters }) {
  const today = new Date();
  const twoMonthsPrevDate = new Date();
  twoMonthsPrevDate.setMonth(twoMonthsPrevDate.getMonth() - 2);

  // Filter parameters
  const [startDate, setStartDate] = useState(twoMonthsPrevDate.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [customer, setCustomer] = useState("");
  const [item, setItem] = useState("");

  useEffect(() => {
    setOpen(true);
  }, []);

  /**
   * Builds the filter query string from the current fields, passes it
   * to the parent and closes the dialog.
   *
   * @param {React.FormEvent} event - The form submit event. Its default page reload is prevented.
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    onChangeFilters(
      "&dateStart=" +
        startDate +
        "&dateEnd=" +
        endDate +
        "&customer=" +
        customer +
        "&item=" +
        item
    );
    onModalClose();
  };

  /**
   * Clears every filter field in the dialog, including both dates.
   *
   * This only changes the dialog's fields.
   * The list is not refetched until the user clicks Apply.
   */
  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setCustomer("");
    setItem("");
  };

  // Rendered content
  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={onModalClose}
        maxWidth="xs"
        fullWidth
        aria-labelledby="order-dialog"
      >
        <form
          autoComplete="off"
          noValidate
          onSubmit={(event) => handleSubmit(event)}
        >
          <DialogTitle id="order-dialog">Add Filters</DialogTitle>
          <DialogContent dividers style={{ overflow: "hidden" }}>
            <Grid
              container
              spacing={3}
              alignItems="flex-start"
              justifyContent="center"
              direction="column"
            >
              {/* Date Selector Title Row */}
              <Grid container item spacing={5}>
                {/* Start date title */}
                <Grid item xs={6}>
                  <Typography component="h2" variant="h6">
                    Start Date
                  </Typography>
                </Grid>
                {/* End date title */}
                <Grid item xs={6}>
                  <Typography component="h3" variant="h6">
                    End Date
                  </Typography>
                </Grid>
              </Grid>
              {/* Date Textfield Row */}
              <Grid item container spacing={5}>
                <Grid item xs={6}>
                  {/* Start Date Textfield */}
                  <TextField
                    variant="outlined"
                    label="From"
                    type="date"
                    value={startDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={(event) => setStartDate(event.target.value)}
                    fullWidth
                  />
                </Grid>
                {/* End Date Textfield */}
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    label="To"
                    type="date"
                    value={endDate}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={(event) => setEndDate(event.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              {/* Textfield titles */}
              <Grid container item spacing={5}>
                {/* Customer code title */}
                <Grid item xs={6}>
                  <Typography component="h4" variant="h6">
                    Customer Code
                  </Typography>
                </Grid>
              </Grid>
              {/* Textfields */}
              <Grid item container spacing={5}>
                {/* Customer Code Textfield */}
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    label="Customer Code"
                    value={customer}
                    onChange={(event) => setCustomer(event.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          {/* Submit and Cancel Buttons */}
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={onModalClose}
            >
              Cancel
            </Button>
            <Button variant="contained" color="secondary" onClick={handleClear}>
              Reset
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Apply
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
}

export default BackOrderDialog;